import {
  CombinedGraphQLErrors,
  LinkError,
  ServerError,
  ServerParseError,
} from '@apollo/client/errors';
import type { GraphQLFormattedError } from 'graphql';
import isRecord from './isRecord';

// Error helpers for Apollo Client 4 errors. Apollo Client 2 wrapped every error in an ApolloError with
// `graphQLErrors` and `networkError`; Apollo Client 4 rejects with CombinedGraphQLErrors (the GraphQL errors of the
// response), ServerError / ServerParseError (HTTP and body problems) or the error a link emitted (LinkError).

const SERVER_CONNECTION_ERROR =
  'Verbindung zum Server derzeit nicht möglich, bitte Internetverbindung prüfen!';

/** The GraphQL errors of a failed operation, or null when it did not fail with GraphQL errors. */
export const graphqlErrorsOf = (error: unknown): ReadonlyArray<GraphQLFormattedError> | null => {
  if (CombinedGraphQLErrors.is(error)) return error.errors;
  return null;
};

/** The operation did not get a GraphQL response: no connection, an HTTP error status or an unreadable body. */
export const isNetworkError = (error: unknown): boolean =>
  ServerError.is(error) || ServerParseError.is(error) || LinkError.is(error);

/** Messages of the GraphQL errors in the body of a non-2xx response. */
const serverErrorMessages = (error: unknown): string | null => {
  if (!ServerError.is(error)) return null;
  try {
    const body: unknown = JSON.parse(error.bodyText);
    if (!isRecord(body) || !Array.isArray(body.errors)) return null;
    const messages = body.errors
      .map((item: unknown) =>
        isRecord(item) && typeof item.message === 'string' ? item.message : null,
      )
      .filter((message): message is string => message !== null);
    return messages.join('\n') || null;
  } catch {
    return null;
  }
};

const toError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  return new Error(String(error));
};

/** An Error with the GraphQL error messages (one per line), the connection message, or the error itself. */
export const formatGraphqlErrors = (error: unknown): Error => {
  const graphQLErrors = graphqlErrorsOf(error);
  const graphqlErrorMsg = graphQLErrors ? graphQLErrors.map((err) => err.message).join('\n') : null;
  const networkErrorMsg = isNetworkError(error) ? SERVER_CONNECTION_ERROR : null;
  const msg = graphqlErrorMsg || serverErrorMessages(error) || networkErrorMsg;
  return msg ? new Error(msg) : toError(error);
};

/**
 * An Error with the GraphQL error messages joined by commas (what `new Error(error.graphQLErrors.map(...))` gave
 * under Apollo Client 2), or the error itself.
 */
export const toGraphqlError = (error: unknown): Error => {
  const graphQLErrors = graphqlErrorsOf(error);
  if (graphQLErrors) return new Error(graphQLErrors.map((err) => err.message).join(','));
  return toError(error);
};

/** An error that carries the original error of the failed operation in `raw`. */
export interface IErrorWithRaw extends Error {
  raw: unknown;
}

/** Attaches the original error to the formatted one (the `e.raw = error` of the untyped code). */
export const withRaw = (error: Error, raw: unknown): IErrorWithRaw => Object.assign(error, { raw });

/** `error.message` of a caught value, which the untyped code read without checking the value. */
export const messageOf = (error: unknown): string | undefined => {
  if (isRecord(error) && typeof error.message === 'string') return error.message;
  if (error instanceof Error) return error.message;
  return undefined;
};
